import React, { useState } from 'react';
import { Badge, Box, FormHelperText, Stack, Tab, Tabs, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { MultilingualString } from '../api/types';
import { useOrg } from '../contexts/OrgContext';
import { REQUIRED_LOCALE, isRtlLocale, localeLabel, type Locale } from '../i18n/contentLocales';
import { isRichTextEmpty } from '../utils/richtext';
import RichTextEditor from './RichTextEditor';

interface MultilingualFieldProps {
  value: MultilingualString;
  onChange: (next: MultilingualString) => void;
  label: string;
  multiline?: boolean;
  /**
   * Render the rich-text editor per locale instead of a text input (spec 049).
   * The value then holds HTML. Legacy plain text is converted on first edit.
   */
  richText?: boolean;
  required?: boolean;
  minRows?: number;
  id?: string;
  helperText?: string;
  placeholder?: string;
}

const MultilingualField: React.FC<MultilingualFieldProps> = ({
  value,
  onChange,
  label,
  multiline = false,
  richText = false,
  required = false,
  minRows = 3,
  id,
  helperText,
  placeholder,
}) => {
  const { t, i18n } = useTranslation('content');
  const { languages } = useOrg();
  // The org's enabled content locales, never empty (German is always required).
  const locales: Locale[] = languages.length ? languages : [REQUIRED_LOCALE];
  const [active, setActive] = useState<Locale>(REQUIRED_LOCALE);
  // If the org disabled the locale we were on, fall back to the first enabled.
  const activeLocale: Locale = locales.includes(active) ? active : locales[0];

  const inputId = id ?? `multilingual-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const isFilled = (locale: Locale): boolean =>
    richText ? !isRichTextEmpty(value[locale]) : !!value[locale]?.trim();
  const isRequiredMissing = required && !isFilled(REQUIRED_LOCALE);

  const isRtl = isRtlLocale(activeLocale);
  const showError = activeLocale === REQUIRED_LOCALE && isRequiredMissing;
  const resolvedHelper =
    helperText ?? (activeLocale === REQUIRED_LOCALE ? undefined : t('multilingual.optionalHint'));

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box component="label" htmlFor={`${inputId}-${activeLocale}`} sx={{ fontWeight: 500 }}>
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </Box>
        <Tabs
          value={activeLocale}
          onChange={(_, next: Locale) => setActive(next)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ minHeight: 36 }}
          aria-label={locales.map((l) => localeLabel(l, i18n.language)).join(' / ')}
        >
          {locales.map((locale) => {
            const filled = isFilled(locale);
            return (
              <Tab
                key={locale}
                value={locale}
                label={
                  <Badge
                    color={locale === REQUIRED_LOCALE && !filled ? 'error' : 'primary'}
                    variant="dot"
                    invisible={filled || locale !== REQUIRED_LOCALE}
                    sx={{ '& .MuiBadge-badge': { right: -6, top: 4 } }}
                  >
                    <span>{localeLabel(locale, i18n.language)}</span>
                  </Badge>
                }
                sx={{
                  minHeight: 36,
                  py: 0,
                  textTransform: 'none',
                  fontSize: 13,
                  // Subtle highlight on the required tab when empty so the
                  // author can't miss it even if they're editing AR/UK.
                  color: locale === REQUIRED_LOCALE && !filled ? 'error.main' : undefined,
                }}
              />
            );
          })}
        </Tabs>
      </Box>
      {richText ? (
        <Box>
          <RichTextEditor
            key={activeLocale}
            id={`${inputId}-${activeLocale}`}
            value={value[activeLocale] ?? ''}
            onChange={(html) => onChange({ ...value, [activeLocale]: html })}
            ariaLabel={`${label} (${localeLabel(activeLocale, i18n.language)})`}
            dir={isRtl ? 'rtl' : 'ltr'}
            error={showError}
            placeholder={placeholder}
          />
          {(resolvedHelper || showError) && (
            <FormHelperText error={showError} sx={{ mx: 1.75 }}>
              {showError ? t('multilingual.requiredHint') : resolvedHelper}
            </FormHelperText>
          )}
        </Box>
      ) : (
        <TextField
          key={activeLocale}
          id={`${inputId}-${activeLocale}`}
          value={value[activeLocale] ?? ''}
          onChange={(e) => onChange({ ...value, [activeLocale]: e.target.value })}
          fullWidth
          multiline={multiline}
          minRows={multiline ? minRows : undefined}
          required={required && activeLocale === REQUIRED_LOCALE}
          error={showError}
          helperText={resolvedHelper}
          placeholder={placeholder}
          slotProps={{
            htmlInput: {
              'aria-label': `${label} (${localeLabel(activeLocale, i18n.language)})`,
              dir: isRtl ? 'rtl' : 'ltr',
            },
          }}
        />
      )}
    </Stack>
  );
};

export default MultilingualField;
